import { AxiosError, AxiosResponse } from "axios";
import { createApi } from "../../../../utils/createApi";
import { universalAPIErrorHandler } from "../../../../utils/universalAPIErrorHandler";
import { markSiteUnsynced } from "../../../../utils/syncStore";

/**
 * The site camera list is paginated; walk every page so large sites don't
 * lose rows after a refresh. Shared with the bulk per-camera motion action.
 */
export const fetchAllSiteCameras = async (
  siteId: number | string
): Promise<any> => {
  const AxiosUI = createApi("");
  let page = 1;
  let all: any = null;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const response: AxiosResponse = await AxiosUI.get(
      `camera/site/?customer__id=${siteId}&page=${page}`
    );
    if (!all) {
      all = response.data;
    } else {
      all.results.push(...response.data.results);
    }
    if (!response.data.next) break;
    page += 1;
  }
  return all;
};

/**
 * Site-wide Milestone motion (ENG-950 / SUP-133).
 *
 * `milestone_motion` is a per-camera flag on the generic (XProtect) Milestone
 * integration — there is no site-level column — so the backend action fans the
 * value out to every camera of the site (or to `cameraIds` when given). The
 * value only reaches the connector on the next Sync, hence markSiteUnsynced().
 */
export const handleSetMilestoneMotion = async ({
  siteId,
  enabled,
  cameraIds,
  navigate,
  setSiteCameras,
  setAlertState,
  setSelected,
  setSyncNeeded,
  setBackdropLoadingScreenOn,
}: {
  siteId: number | string;
  enabled: boolean;
  cameraIds?: number[];
  navigate: any;
  setSiteCameras: any;
  setAlertState: any;
  setSelected: any;
  setSyncNeeded: any;
  setBackdropLoadingScreenOn: any;
}) => {
  const AxiosUI = createApi("");
  const retryFunction = () => {
    handleSetMilestoneMotion({
      siteId,
      enabled,
      cameraIds,
      navigate,
      setSiteCameras,
      setAlertState,
      setSelected,
      setSyncNeeded,
      setBackdropLoadingScreenOn,
    });
  };

  const payload: { enabled: boolean; camera_ids?: number[] } = { enabled };
  if (cameraIds && cameraIds.length > 0) {
    payload.camera_ids = cameraIds;
  }

  let failed = false;
  await AxiosUI.post(`customer/${siteId}/milestone_motion/`, payload).then(
    () => {
      failed = false;
    },
    (reason: AxiosError) => {
      failed = true;
      universalAPIErrorHandler(reason, navigate, retryFunction, () => {
        const detail = (reason.response?.data as any)?.detail;
        alert(
          detail
            ? `Milestone motion update failed: ${detail}`
            : "Milestone motion update failed, please try again."
        );
        setBackdropLoadingScreenOn(false);
      });
    }
  );
  if (failed) return;

  await fetchAllSiteCameras(siteId).then(
    (siteCameras: any) => {
      localStorage.setItem("siteCameras", JSON.stringify(siteCameras));
      setSiteCameras(siteCameras);
      markSiteUnsynced();
      setSyncNeeded(true);
      setAlertState((previousState: any) => ({
        ...previousState,
        changeSuccess: true,
        activateSuccess: false,
        deactivateSuccess: false,
        disarmSuccess: false,
        armSuccess: false,
        syncSuccess: false,
        cameraDeleteSuccess: false,
      }));
      setSelected([]);
      setBackdropLoadingScreenOn(false);
    },
    (reason: AxiosError) => {
      universalAPIErrorHandler(reason, navigate, retryFunction, () => {
        // The update itself succeeded — still flag the sync even if the
        // table refresh failed.
        markSiteUnsynced();
        setSyncNeeded(true);
        setBackdropLoadingScreenOn(false);
      });
    }
  );
};
